import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { Autocomplete, TextField } from "@mui/material";
import {
  DatePicker,
  FormLabel,
  ModalSuccess,
  ModalError,
  PrimaryButton,
  SecondaryButton,
  MultipleSelectInputWithTags,
} from "components";
import { handleChangeFilterAll, getErrors, arrayToCommaSeparatedString, stringTruncate } from "utils";
import moment from "moment";
import { useEffect, useState } from "react";
import {
  mockDataPeriodOptions,
  mockPublisherOptions,
  mockData,
} from "./mockData";
import axios from "axios";
import { hardBaseUrl } from "services/urlConstant";

const ModalFormUnclaimOpenPeriod = ({
  open,
  onClose,
  preload,
  getDataTable,
  viewOnly = false,
}) => {
  const [loadingButton, setLoadingButton] = useState(false);
  const [payload, setPayload] = useState({
    dsp_id: "",
    data_period_ids: [],
    publisher_ids: [],
    start_date: null,
    end_date: null,
  });
  const [searchPeriod, setSearchPeriod] = useState("");
  const [searchPublisher, setSearchPublisher] = useState("");
  const [dspOptions, setDspOptions] = useState([]);

  useEffect(() => {
    const fetchDspOptions = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(
          `${hardBaseUrl}/claim-configuration/filter-dsp`,
          { headers }
        );
        setDspOptions(res?.data?.data || []);
      } catch (error) {
        console.error("Error fetching DSP options:", error);
      }
    };
    if (open) {
      fetchDspOptions();
    }
  }, [open]);

  const [dataPeriodOptions, setDataPeriodOptions] = useState([]);
  const [publisherOptions, setPublisherOptions] = useState([]);

  useEffect(() => {
    const fetchDataPeriodOptions = async () => {
      if (!payload.dsp_id) {
        setDataPeriodOptions([]);
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(
          `${hardBaseUrl}/claim-configuration/filter-period/${payload.dsp_id}`,
          { headers }
        );
        const fetched = res?.data?.data || [];
        const formatted = fetched.map((p, i) => ({
          ...p,
          id: p.id || `${p.start_date}_${p.end_date}` || i,
          label: p.label || p.name || (p.start_date ? `${moment(p.start_date).format("DD MMMM YYYY")} - ${moment(p.end_date).format("DD MMMM YYYY")}` : ""),
        }));
        setDataPeriodOptions(formatted);
      } catch (error) {
        console.error("Error fetching data period options:", error);
      }
    };
    const fetchPublisherOptions = async () => {
      if (!payload.dsp_id) {
        setPublisherOptions([]);
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(
          `${hardBaseUrl}/claim-configuration/filter-publisher/${payload.dsp_id}`,
          { headers }
        );
        const fetched = res?.data?.data || [];
        const formatted = fetched.map((p, i) => ({
          ...p,
          id: p.publisher_id || p.id || i,
          name: p.publisher_name || p.name || "",
        }));
        setPublisherOptions(formatted);
      } catch (error) {
        console.error("Error fetching publisher options:", error);
      }
    };
    if (open) {
      fetchDataPeriodOptions();
      fetchPublisherOptions();
    }
  }, [open, payload.dsp_id]);

  const handleChangePayload = (value, key) => {
    setPayload(currentState => ({
      ...currentState,
      [key]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoadingButton(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const selectedPeriods = payload.data_period_ids
        .filter(opt => opt.id !== "all")
        .map(opt => ({
          start: opt.start_date ? moment(opt.start_date).format("YYYY-MM-DD") : "",
          end: opt.end_date ? moment(opt.end_date).format("YYYY-MM-DD") : "",
        }));

      const publisher_ids = payload.publisher_ids
        .filter(opt => opt.id !== "all")
        .map(opt => opt.id);

      const body = {
        dsp_id: payload.dsp_id,
        active_start: payload.start_date ? moment(payload.start_date).format("YYYY-MM-DD") : "",
        active_end: payload.end_date ? moment(payload.end_date).format("YYYY-MM-DD") : "",
        periods: selectedPeriods,
        publisher_ids,
      };

      if (preload) {
        await axios.put(`${hardBaseUrl}/claim-configuration/${preload}`, body, { headers });
      } else {
        await axios.post(`${hardBaseUrl}/claim-configuration`, body, { headers });
      }

      onClose();
      ModalSuccess(`Successfully ${preload ? "updated" : "created"} unclaim open period`).then(() => {
        getDataTable();
      });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingButton(false);
    }
  };

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${hardBaseUrl}/claim-configuration/${preload}`, { headers });
        const detail = res?.data?.data;
        if (detail) {
          setPayload({
            dsp_id: detail.dsp_id || "",
            data_period_ids: (detail.periods || []).map((p, i) => ({
              ...p,
              id: p.id || `${p.start_date}_${p.end_date}` || i,
              label: p.label || p.name || (p.start_date ? `${moment(p.start_date).format("DD MMMM YYYY")} - ${moment(p.end_date).format("DD MMMM YYYY")}` : ""),
            })),
            publisher_ids: (detail.publishers || []).map((p, i) => ({
              ...p,
              id: p.publisher_id || p.id || i,
              name: p.publisher_name || p.name || "",
            })),
            start_date: detail.active_start ? moment(detail.active_start) : null,
            end_date: detail.active_end ? moment(detail.active_end) : null,
          });
        }
      } catch (error) {
        console.error("Error fetching detail:", error);
      }
    };

    if (open) {
      if (preload) {
        fetchDetail();
      } else {
        setPayload({
          dsp_id: "",
          data_period_ids: [],
          publisher_ids: [],
          start_date: null,
          end_date: null,
        });
      }
    }
  }, [open, preload]);

  const selectedDsp =
    dspOptions.find(d => (d.dsp_id || d.id) === payload.dsp_id) || null;

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>{viewOnly ? "Detail Unclaim Open Period" : "Unclaim Open Period"}</DialogTitle>
      <DialogContent dividers>
        <Grid container direction="column" rowSpacing={2}>
          <Grid item>
            <FormLabel label="DSP" />
            <Autocomplete
              size="small"
              disabled={viewOnly}
              options={dspOptions}
              getOptionLabel={option => option?.dsp_name || option?.name || ""}
              value={selectedDsp}
              onChange={(_, newValue) =>
                handleChangePayload(newValue?.dsp_id || newValue?.id || "", "dsp_id")
              }
              renderInput={params => (
                <TextField {...params} placeholder="DSP" />
              )}
            />
          </Grid>
          <Grid item>
            <FormLabel label="Data Period" />
            <MultipleSelectInputWithTags
              placeholder="Select Data Period"
              disabled={viewOnly || !payload.dsp_id}
              value={payload.data_period_ids}
              textValue={stringTruncate(
                arrayToCommaSeparatedString(payload?.data_period_ids, "label"),
                40
              )}
              options={[{ id: "all", label: "All" }, ...dataPeriodOptions].filter(
                opt =>
                  (opt.label || opt.name || "")
                    .toLowerCase()
                    .includes(searchPeriod.toLowerCase())
              )}
              optionKey="id"
              optionLabel="label"
              search={true}
              searchValue={searchPeriod}
              onChangeSearch={setSearchPeriod}
              onChange={(event, option) => {
                const checked = event?.target ? event.target.checked : false;
                handleChangePayload(
                  handleChangeFilterAll({
                    selectedList: payload.data_period_ids,
                    checked,
                    option,
                    key: "id",
                    list: [{ id: "all", label: "All" }, ...dataPeriodOptions],
                    handleChangeQueryParams: () => {}, // dummy
                  }),
                  "data_period_ids"
                );
              }}
              width="100%"
            />
          </Grid>
          <Grid item>
            <FormLabel label="Publisher" />
            <MultipleSelectInputWithTags
              placeholder="Select Publisher"
              disabled={viewOnly || !payload.dsp_id}
              value={payload.publisher_ids}
              textValue={stringTruncate(
                arrayToCommaSeparatedString(payload?.publisher_ids, "name"),
                40
              )}
              options={[{ id: "all", name: "All" }, ...publisherOptions].filter(
                opt =>
                  (opt.name || "")
                    .toLowerCase()
                    .includes(searchPublisher.toLowerCase())
              )}
              optionKey="id"
              optionLabel="name"
              search={true}
              searchValue={searchPublisher}
              onChangeSearch={setSearchPublisher}
              onChange={(event, option) => {
                const checked = event?.target ? event.target.checked : false;
                handleChangePayload(
                  handleChangeFilterAll({
                    selectedList: payload.publisher_ids,
                    checked,
                    option,
                    key: "id",
                    list: [{ id: "all", name: "All" }, ...publisherOptions],
                    handleChangeQueryParams: () => {}, // dummy
                  }),
                  "publisher_ids"
                );
              }}
              width="100%"
            />
          </Grid>

          <Grid item>
            <FormLabel label="Start Date" />
            <DatePicker
              disabled={viewOnly}
              value={payload.start_date}
              onChange={newValue =>
                handleChangePayload(newValue, "start_date")
              }
              maxDate={payload.end_date ? moment(payload.end_date) : undefined}
              shouldDisableDate={date => {
                if (payload.end_date) {
                  return moment(date).isAfter(moment(payload.end_date), "day");
                }
                return false;
              }}
            />
          </Grid>
          <Grid item>
            <FormLabel label="End Date" />
            <DatePicker
              disabled={viewOnly}
              value={payload.end_date}
              onChange={newValue =>
                handleChangePayload(newValue, "end_date")
              }
              minDate={payload.start_date ? moment(payload.start_date) : undefined}
              shouldDisableDate={date => {
                if (payload.start_date) {
                  return moment(date).isBefore(moment(payload.start_date), "day");
                }
                return false;
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {viewOnly ? (
          <PrimaryButton label="Close" onClick={onClose} />
        ) : (
          <>
            <SecondaryButton label="Cancel" onClick={onClose} />
            <PrimaryButton
              label={loadingButton ? "Saving" : "Save"}
              loading={loadingButton}
              disabled={loadingButton}
              onClick={handleSubmit}
            />
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ModalFormUnclaimOpenPeriod;
